import { create } from 'zustand'

// ============================================================================
// Types
// ============================================================================

interface Task {
  id: string
  titulo: string
  prompt: string
  status: string
  prioridade: number
  projeto?: string
  parent_task_id?: string
  depends_on?: string[]
  execution_order?: number
  created_at: string
  started_at?: string
  finished_at?: string
  subtask_count?: number
}

interface TaskStore {
  tasks: Task[]
  loading: boolean
  error: string | null
  currentTaskId: string | null

  // Actions
  getTasks: () => Promise<void>
  getTask: (id: string) => Promise<Task | null>
  createTask: (data: Partial<Task>) => Promise<string | null>
  createSubtask: (parentId: string, data: Partial<Task>) => Promise<string | null>
  updateTaskStatus: (id: string, status: string) => Promise<boolean>
  approveTask: (id: string) => Promise<boolean>
  rejectTask: (id: string, motivo: string) => Promise<boolean>
  setCurrentTask: (id: string | null) => void
}

// ============================================================================
// API Base URL
// ============================================================================

const API_URL = import.meta.env.PROD
  ? 'https://api.lldonha.com/api'
  : 'https://api.lldonha.com/api'

// ============================================================================
// Store
// ============================================================================

export const useTaskStore = create<TaskStore>((set, get) => ({
  tasks: [],
  loading: false,
  error: null,
  currentTaskId: null,

  // ========================================================================
  // Actions
  // ========================================================================

  getTasks: async () => {
    set({ loading: true, error: null })

    try {
      const response = await fetch(`${API_URL}/tasks`)
      const data = await response.json()

      if (data.success) {
        set({ tasks: data.tasks, loading: false })
      } else {
        throw new Error('Failed to fetch tasks')
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Unknown error',
        loading: false
      })
    }
  },

  getTask: async (id: string) => {
    try {
      const response = await fetch(`${API_URL}/tasks/${id}`)
      const data = await response.json()

      if (data.success) {
        return data.task
      }
      return null
    } catch (error) {
      console.error('Error fetching task:', error)
      return null
    }
  },

  createTask: async (taskData: Partial<Task>) => {
    set({ loading: true, error: null })

    try {
      const response = await fetch(`${API_URL}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titulo: taskData.titulo,
          prompt: taskData.prompt,
          prioridade: taskData.prioridade || 5,
          projeto: taskData.projeto,
          source: 'web',
          username: 'Lucas'
        })
      })

      const data = await response.json()

      if (data.success) {
        // Refresh tasks
        await get().getTasks()
        set({ loading: false })
        return data.task_id
      } else {
        throw new Error('Failed to create task')
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Unknown error',
        loading: false
      })
      return null
    }
  },

  createSubtask: async (parentId: string, taskData: Partial<Task>) => {
    set({ loading: true, error: null })

    try {
      const response = await fetch(`${API_URL}/tasks/subtask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parent_id: parentId,
          titulo: taskData.titulo,
          prompt: taskData.prompt,
          depends_on: taskData.depends_on || [],
          execution_order: taskData.execution_order || 1,
          prioridade: taskData.prioridade || 5,
          projeto: taskData.projeto,
          source: 'web',
          username: 'Lucas'
        })
      })

      const data = await response.json()

      if (data.success) {
        // Refresh tasks
        await get().getTasks()
        set({ loading: false })
        return data.task_id
      } else {
        throw new Error('Failed to create subtask')
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Unknown error',
        loading: false
      })
      return null
    }
  },

  updateTaskStatus: async (id: string, status: string) => {
    try {
      // TODO: Implement status update endpoint
      console.log(`Updating task ${id} to status ${status}`)

      // Optimistic update
      set(state => ({
        tasks: state.tasks.map(task =>
          task.id === id ? { ...task, status } : task
        )
      }))

      return true
    } catch (error) {
      console.error('Error updating task status:', error)
      return false
    }
  },

  approveTask: async (id: string) => {
    try {
      const response = await fetch(`${API_URL}/tasks/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewer: 'Lucas' })
      })

      const data = await response.json()

      if (data.success) {
        await get().getTasks()
        return true
      }
      return false
    } catch (error) {
      console.error('Error approving task:', error)
      return false
    }
  },

  rejectTask: async (id: string, motivo: string) => {
    try {
      const response = await fetch(`${API_URL}/tasks/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo, reviewer: 'Lucas' })
      })

      const data = await response.json()

      if (data.success) {
        await get().getTasks()
        return true
      }
      return false
    } catch (error) {
      console.error('Error rejecting task:', error)
      return false
    }
  },

  setCurrentTask: (id: string | null) => {
    set({ currentTaskId: id })
  }
}))
